import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import RenewalResetButton from "@/components/admin/RenewalResetButton";
import { toast } from "sonner";

const fmtEnv = (e) => (e?.defined ? String(e.value) : "TANIMSIZ");

function Row({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 text-sm">
      <span className="text-slate-600">{label}</span>
      <span className="font-mono text-slate-900" data-testid={testId}>{value}</span>
    </div>
  );
}

function RuntimeCard({ rt }) {
  return (
    <SectionCard title="Runtime Ortam Değerleri">
      <div className="px-5 py-2 divide-y divide-slate-100" data-testid="renewal-diag-runtime">
        <Row label="SUBSCRIPTION_RENEWAL_ENABLED" value={fmtEnv(rt.SUBSCRIPTION_RENEWAL_ENABLED)} testId="diag-env-enabled" />
        <Row label="SUBSCRIPTION_RENEWAL_DRY_RUN" value={fmtEnv(rt.SUBSCRIPTION_RENEWAL_DRY_RUN)} testId="diag-env-dry-run" />
        <Row label="PAYTR_ENABLE_STORE_CARD" value={fmtEnv(rt.PAYTR_ENABLE_STORE_CARD)} testId="diag-env-store-card" />
        <Row label="PAYTR_TEST_MODE" value={fmtEnv(rt.PAYTR_TEST_MODE)} testId="diag-env-test-mode" />
        <Row label="Etkin: yenileme açık mı?" value={String(rt.effective_renewal_enabled)} testId="diag-effective-enabled" />
        <Row label="Etkin: dry run mı?" value={String(rt.effective_dry_run)} testId="diag-effective-dry-run" />
        <Row label="SUBSCRIPTION_RENEWAL_CRON_SECRET" value={rt.SUBSCRIPTION_RENEWAL_CRON_SECRET} testId="diag-renewal-cron-secret" />
        <Row label="TRIAL_MAILS_CRON_SECRET (yalnızca deneme mailleri)" value={rt.TRIAL_MAILS_CRON_SECRET} testId="diag-cron-secret" />
        <Row label="ADMIN_CRON_SECRET" value={rt.ADMIN_CRON_SECRET} testId="diag-admin-cron-secret" />
        <Row label="PayTR anahtarları" value={rt.paytr_configured ? "VAR" : "YOK"} testId="diag-paytr-configured" />
        <Row label="Railway ortam / servis / commit" value={`${rt.railway_environment || "—"} / ${rt.railway_service || "—"} / ${rt.railway_commit || "—"}`} testId="diag-railway" />
      </div>
    </SectionCard>
  );
}

function CronCard({ cron, history }) {
  return (
    <SectionCard title="Cron Çalışma Kayıtları">
      <div className="p-5 space-y-2 text-sm" data-testid="renewal-diag-cron">
        <div>Kayıtlı çalışma sayısı: <b data-testid="diag-cron-run-count">{cron.heartbeat_runs_recorded}</b></div>
        <div>Yenileme/retry ödeme kaydı: <b>{history.renewal_or_retry_payment_records}</b> · Kilitli (denenmiş) firma: <b>{history.companies_with_renewal_locks}</b></div>
        <div className="text-xs text-slate-500">{cron.note}</div>
        {cron.last_runs.map((r) => (
          <div key={r.id} className="font-mono text-xs text-slate-700">
            {r.at} · {r.actor} · {r.status || "completed"} · dry_run={String(r.dry_run)} · taranan={r.scanned} · ok={r.charged_ok} · fail={r.charged_fail}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function CompanyCard({ c, live, onReset }) {
  const r = c.renewal || {};
  const last = r.last_attempt;
  const ready = r.decision === "charge";
  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-2 text-sm" data-testid={`diag-company-${c.company_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium text-slate-900">
          {c.company_name || c.company_id}
          {r.renewal_exempt && <span className="ml-2 text-xs px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200" data-testid={`diag-exempt-${c.company_id}`}>İç test hesabı — yenileme kapalı</span>}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-md border ${ready ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid={`diag-decision-${c.company_id}`}>
          {!c.next_payment_date ? "Vade tarihi yok" : c.due ? "Vadesi geldi" : "Vadesi gelmedi"} · karar: {r.decision}{r.code ? ` (${r.code})` : ""}
        </span>
      </div>
      <div className="text-xs text-slate-600 font-mono">
        durum={c.subscription_status} · plan={c.plan || "—"} · son ödeme={c.last_payment_date || "—"} · sonraki={c.next_payment_date || "—"} · anchor={r.billing_anchor || "—"} · dönem={r.billing_period || "—"}
      </div>
      <div className="text-xs text-slate-600 font-mono">
        tutar={r.amount ?? "GEÇERSİZ"} ({r.price_source || "—"}; güncel={r.current_plan_amount ?? "—"}, önceki={r.previous_amount ?? "—"}) · utoken={r.utoken ? "VAR" : "YOK"} · ctoken={r.ctoken ? "VAR" : "YOK"} · müşteri IP={r.user_ip ? "VAR" : "YOK"} · deneme={r.attempt_count}/{r.max_attempts}
      </div>
      <div className="text-xs text-slate-600 font-mono" data-testid={`diag-last-attempt-${c.company_id}`}>
        son deneme: {last ? `#${last.attempt_number} ${last.status}${last.error_code ? ` (${last.error_code})` : ""}${last.failure_category ? ` · kategori=${last.failure_category}` : ""} · ${last.created_at}${last.reset_at ? ` · sıfırlandı ${last.reset_at}` : ""}` : "—"}
      </div>
      {last?.failure_reason && <div className="text-xs text-rose-600" data-testid={`diag-failure-reason-${c.company_id}`}>Hata: {last.failure_reason}</div>}
      {["permanent_failure", "merchant_configuration_error"].includes(r.code) && (
        <RenewalResetButton company={c} live={live} onDone={onReset} />
      )}
      <details>
        <summary className="text-xs text-slate-500 cursor-pointer">Ödemeler ve olay kayıtları</summary>
        <pre className="text-[11px] bg-slate-50 rounded p-2 overflow-x-auto">{JSON.stringify({ payments: c.payments, events: c.subscription_events }, null, 2)}</pre>
      </details>
    </div>
  );
}

export default function AdminRenewalDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/subscriptions/renewal-diagnostics");
      setData(d);
    } catch (e) {
      toast.error("Teşhis verisi alınamadı");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-renewal-diagnostics">
      <PageHeader
        title="Yenileme Teşhisi"
        subtitle="Salt okunur — hiçbir veri değişmez, PayTR çağrısı yapılmaz"
        action={
          <Button variant="outline" onClick={load} disabled={loading} className="h-10 rounded-lg gap-2" data-testid="renewal-diag-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Yenile
          </Button>
        }
      />
      {data && (
        <>
          <RuntimeCard rt={data.runtime} />
          <CronCard cron={data.cron} history={data.history} />
          <SectionCard title={`Abonelikler (${data.companies.length}) · Bugünkü aday: ${data.candidates_today.length}`}>
            <div className="p-5 space-y-3">
              {data.companies.map((c) => <CompanyCard key={c.company_id} c={c} live={!data.runtime.effective_dry_run} onReset={load} />)}
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
